import { Box, Button, Card, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { FilterKeys, SavedFilter, useGetFiltersQuery } from '@schooly/api';
import { FilterSection } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PageHeader } from '@schooly/components/filters';
import {
  ChevronRightIcon,
  CreateMarkbookSvg,
  EmptyFolderSvg,
  EmptyPresetsSvg,
  Loading,
  PlusIcon,
  SimpleCard,
  TruncatedTagSelect,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { compareDesc } from 'date-fns';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useRouter } from '../../../context/router/useRouter';
import useUserCounts from '../../../hooks/useUserCounts';
import { AccessDenied } from '@schooly/style';
import { useMarkbookFilterSummary } from './MarkbookEditor/helpers';
import { canViewMarkbook } from './permissions';

const ADD_ASSESSMENT_DEFAULT_PAGE = 'assessments';

export const MarkbookContent: FC = () => {
  const { permissions, schoolId = '', currentStaff } = useAuth();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { setBackground } = useRouter();
  const { userCounts, fetching: isLoadingUserCounts } = useUserCounts();

  const { data: filters, isLoading: isLoadingFilters } = useGetFiltersQuery(
    {
      schoolId: schoolId,
      relationId: currentStaff?.relation_id ?? '',
      section: FilterSection.Markbook,
    },
    { enabled: !!currentStaff?.relation_id && !!schoolId, refetchOnMount: 'always' },
  );

  const sortedFilters = useMemo(() => {
    return filters?.sort((a, b) => {
      const aDate = newDateTimezoneOffset(a.activity_date);
      const bDate = newDateTimezoneOffset(b.activity_date);
      return compareDesc(aDate, bDate);
    });
  }, [filters]);

  const hasAccess = canViewMarkbook(permissions);
  const hasAssessments = !!userCounts?.assessment;

  if (!hasAccess) {
    return <AccessDenied />;
  }

  if (isLoadingUserCounts) {
    return (
      <>
        <PageHeader pageTitleTextId="section-Markbook" />
        <Loading />
      </>
    );
  }

  if (!hasAssessments) {
    return (
      <>
        <PageHeader pageTitleTextId="section-Markbook" />
        <MarkbookContentEmptyStub
          textId="assessments-markbook-noAssessmentsForSchool"
          subTextId="assessments-markbook-createAssessments"
          onClick={() => {
            setBackground(ADD_ASSESSMENT_DEFAULT_PAGE);
            navigate('/assessments/new');
          }}
        />
      </>
    );
  }

  const noAvailableMarkbooks = !filters?.length;

  return (
    <>
      <PageHeader pageTitleTextId="section-Markbook" />
      <Stack alignItems="center" pt={2.5} gap={7.5}>
        <Stack alignItems="center">
          <Typography variant="h1">
            {formatMessage({ id: 'assessments-startWithMarkbook' })}
          </Typography>

          <Typography variant="h3" pt={1} color="common.grey2">
            {formatMessage({ id: 'assessments-startWithMarkbookDescription' })}
          </Typography>
        </Stack>

        <Stack gap={2}>
          <MarkbookCard
            title={formatMessage({ id: 'assessments-createNewMarkbookButton' })}
            description={formatMessage({ id: 'assessments-createNewMarkbookButtonDescription' })}
            icon={<CreateMarkbookSvg />}
            onClick={() => navigate('/markbook/filters')}
          />

          <Card
            sx={{
              width: 700,
              maxHeight: noAvailableMarkbooks ? undefined : 370,
              opacity: noAvailableMarkbooks ? 0.5 : 1,
            }}
          >
            <Stack sx={{ height: '100%' }}>
              <Stack direction="row" pl={5.5} pt={3} pb={2} gap={1.5}>
                <Stack width={60} height={54}>
                  <EmptyPresetsSvg />
                </Stack>
                <Stack gap={1}>
                  <Typography variant="h2">
                    {isLoadingFilters ? (
                      <Skeleton variant="text" width={250} />
                    ) : (
                      formatMessage({
                        id: noAvailableMarkbooks
                          ? 'assessments-noSavedMarkbooks'
                          : 'assessments-markbook-openMarkbook',
                      })
                    )}
                  </Typography>

                  <Typography variant="h3" color="common.main3" whiteSpace="nowrap">
                    {isLoadingFilters ? (
                      <Skeleton variant="text" width={300} />
                    ) : (
                      formatMessage({
                        id: noAvailableMarkbooks
                          ? 'assessments-noSavedMarkbooksDescription'
                          : 'assessments-markbook-openMarkbookDescription',
                      })
                    )}
                  </Typography>
                </Stack>
              </Stack>

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                  px: 2,
                  pb: 1.25,
                }}
              >
                {sortedFilters?.map((filter) => (
                  <FilterCard key={filter.id} filter={filter} />
                ))}
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </>
  );
};

type FilterCardProps = {
  filter: SavedFilter;
};

const FilterCard: FC<PropsWithChildren<FilterCardProps>> = ({ filter }) => {
  const navigate = useNavigate();

  const {
    dateLabel,
    ageGroupTags,
    groupTags,
    subjectTags,
    aolTags,
    tutorGroupLabel,
    tutorFeedbackLabel,
  } = useMarkbookFilterSummary({
    filter: {
      date: filter.filter[FilterKeys.Date] ?? [],
      groupIds: filter.filter[FilterKeys.Group] ?? [],
      ageGroupIds: filter.filter[FilterKeys.AgeGroup] ?? [],
      groupTypes: filter.filter[FilterKeys.GroupTypes] ?? [],
      subjectIds: filter.filter[FilterKeys.Subject] ?? [],
      aolIds: filter.filter[FilterKeys.AreaOfLearning] ?? [],
      evaluationTypes: filter.filter[FilterKeys.EvaluationType] ?? [],
    },
    tagProps: { size: 'small', sx: { maxWidth: 200 } },
  });

  return (
    <SimpleCard
      withBorder
      sx={{ width: 660, my: 1, borderRadius: (theme) => theme.spacing(1) }}
      onClick={() => navigate(`/markbook/${filter.id}`)}
      actions={
        <IconButton sx={{ pt: 2.5, alignSelf: 'end', color: 'common.grey' }}>
          <ChevronRightIcon />
        </IconButton>
      }
    >
      <Stack gap={1.25} pt={0.5} width="100%">
        <TypographyWithOverflowHint variant="h3">{filter.name}</TypographyWithOverflowHint>
        <Stack direction="row" alignItems="center" gap={1} maxWidth={500}>
          <TruncatedTagSelect
            spacing={2}
            items={[
              dateLabel,
              ...ageGroupTags,
              ...groupTags,
              ...(tutorGroupLabel ? [tutorGroupLabel] : []),
              ...subjectTags,
              ...(tutorFeedbackLabel ? [tutorFeedbackLabel] : []),
              ...aolTags,
            ]}
            tooltipProps={{
              componentsProps: { tooltip: { sx: { maxWidth: 'unset' } } },
            }}
          />
        </Stack>
      </Stack>
    </SimpleCard>
  );
};

type MarkbookCardProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

const MarkbookCard: FC<MarkbookCardProps> = ({ title, description, icon, onClick, disabled }) => {
  return (
    <SimpleCard
      withBorder
      sx={{
        width: 700,
        borderRadius: (theme) => theme.spacing(1),
      }}
      avatar={<Box pl={1.5}>{icon}</Box>}
      disabled={disabled}
      onClick={onClick}
      actions={
        onClick ? (
          <IconButton sx={{ mt: 4.25, ml: 2, alignSelf: 'center', color: 'common.grey' }}>
            <ChevronRightIcon />
          </IconButton>
        ) : null
      }
    >
      <Stack gap={1} py={1.75}>
        <Typography variant="h2">{title}</Typography>
        <Typography variant="h3" color="common.main3" whiteSpace="nowrap">
          {description}
        </Typography>
      </Stack>
    </SimpleCard>
  );
};

type MarkbookContentEmptyStubProps = {
  textId: string;
  subTextId: string;
  onClick: () => void;
};

const MarkbookContentEmptyStub: FC<MarkbookContentEmptyStubProps> = ({
  textId,
  subTextId,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={1} justifyContent="center" alignItems="center" position="relative" height="100%">
      <Box sx={{ svg: { width: 300 } }}>
        <EmptyFolderSvg />
      </Box>
      <Typography variant="h1" textAlign="center" pt={7} sx={{ whiteSpace: 'pre-wrap' }}>
        {formatMessage({ id: textId })}
      </Typography>
      <Typography variant="h3" color="text.primary" textAlign="center">
        {formatMessage({ id: subTextId })}
      </Typography>
      <Button onClick={onClick} sx={{ mt: 2 }} startIcon={<PlusIcon />}>
        {formatMessage({ id: 'assessments-CreateAssessment' })}
      </Button>
    </Stack>
  );
};
